import MeridianColumn from '@amzn/meridian/column';
import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import { color } from '../../theme/colors';
import { ResellerClaimRegexPattern } from '../../types';
import { toMilliseconds } from '../../utils/dateUtils';
import { Button, useTranslation } from '../blocks';
import { SuperPaginatedTable, SuperPaginatedTableColumn } from '../constructed/SuperPaginatedTable';

interface Properties {
    patterns: ResellerClaimRegexPattern[];
    onDeletePattern: (pattern: ResellerClaimRegexPattern) => void;
    canEdit: boolean;
}

const BorderedColumn = styled(MeridianColumn)`
    border: 1px solid ${color.gray[300]};
    box-shadow: 0px 0px 3px ${color.gray[300]};
    overflow-x: auto;
`;

const formatTimestamp = (value: string | number): string => {
    if (!value && value !== 0) {
        return '-';
    }
    try {
        return new Date(toMilliseconds(value)).toISOString().split('T')[0];
    } catch {
        return String(value);
    }
};

const sortTimestamp = (value: string | number): string => {
    if (!value && value !== 0) return '0';
    try {
        const numValue = typeof value === 'number' ? value : Number(value);
        return String(numValue).padStart(20, '0');
    } catch {
        return String(value).padStart(20, '0');
    }
};

const joinList = (value: string[]): string => (value ? value.join(', ') : '');

export const ResellerClaimRegexTable: React.FC<Properties> = ({ patterns, onDeletePattern, canEdit }) => {
    const { t } = useTranslation('resellerClaimRegexTable');
    const { t: tCommon } = useTranslation('common');

    const columns: SuperPaginatedTableColumn[] = useMemo(() => {
        const cols: SuperPaginatedTableColumn[] = [
            { sourceProperty: 'countryCode', title: t('countryCode-columnHeader') },
            { sourceProperty: 'regexPattern', title: t('regexPattern-columnHeader'), isKey: true },
            { sourceProperty: 'regexDescription', title: t('regexDescription-columnHeader') },
            {
                sourceProperty: 'subResellerName',
                title: t('subReseller-columnHeader'),
                format: {
                    render: (value: string) => value || '',
                    sort: (value: string) => value || '',
                    search: (value: string) => value || '',
                },
            },
            {
                sourceProperty: 'examples',
                title: t('examples-columnHeader'),
                format: {
                    render: joinList,
                    sort: joinList,
                    search: joinList,
                },
            },
            {
                sourceProperty: 'createdAt',
                title: t('createdAt-columnHeader'),
                defaultSort: 'descending',
                format: {
                    render: (value: string | number) => formatTimestamp(value),
                    sort: (value: string | number) => sortTimestamp(value),
                    search: (value: string | number) => formatTimestamp(value),
                },
            },
            { sourceProperty: 'updatedBy', title: t('updatedBy-columnHeader') },
        ];

        if (canEdit) {
            cols.push({
                sourceProperty: 'actions',
                id: 'actions',
                title: t('actions-columnHeader'),
                format: {
                    render: (_value: any, row: ResellerClaimRegexPattern) => (
                        <Button
                            type={'secondary'}
                            size={'small'}
                            onClick={() => onDeletePattern(row)}
                            data-testid={`delete-pattern-${row.regexPattern}`}
                        >
                            {tCommon('delete-text')}
                        </Button>
                    ),
                    sort: () => '',
                    search: () => '',
                },
                searchable: false,
            });
        }

        return cols;
    }, [t, tCommon, canEdit, onDeletePattern]);

    return (
        <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
            <SuperPaginatedTable
                dataTestId={'reseller-regex-table'}
                data={patterns}
                layout={{
                    pageSize: 10,
                    searchPlaceholder: t('findPatterns-searchPlaceholder'),
                    columns,
                }}
            />
        </BorderedColumn>
    );
};
